import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Bot,
  Check,
  ChevronRight,
  FileText,
  FileWarning,
  FileX2,
  Flag,
  Keyboard,
  Landmark,
  Pencil,
  Repeat2,
  CircleDashed,
  Scale,
  Sparkles,
  UserRoundCheck,
  X,
} from "lucide-react"

import { ConfidenceMeter, confidenceTone } from "@/components/ConfidenceMeter"
import { BotonComentario } from "@/components/Comentarios"
import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { SinComprobante } from "@/routes/review/SinComprobante"
import { ReasonDialog } from "@/components/ReasonDialog"
import { SearchInput, Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  stickyActionCell,
  stickyActionHead,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import { momento } from "@/lib/marty"
import { useVoz } from "@/lib/voz"
import { SUSTENTO_META, NATURALEZA_LABELS } from "@/lib/types"
import type { ClassificationSource, EstadoSustento, ReviewItem } from "@/lib/types"
import { useTableKeyboardNav } from "@/lib/useTableKeyboardNav"
import { cn } from "@/lib/utils"
import { refDocumento } from "@/routes/comentarios/api"

import { CorrectionDialog } from "@/routes/review/CorrectionDialog"
import { onWriteError } from "@/lib/writeErrors"

import { fetchAccounts } from "@/routes/books/api"
import { fetchCostCenters } from "@/routes/masters/api"
import {
  approveClassification,
  bulkApprove,
  correctClassification,
  fetchReviewQueue,
  type CorrectionPayload,
} from "@/routes/review/api"
import { BotonDescargar } from "@/components/BotonDescargar"

/**
 * De donde salio la propuesta. Es lo primero que mira quien revisa: una regla
 * escrita por una persona y una inferencia del modelo NO merecen la misma
 * atencion, aunque el porcentaje de confianza se parezca.
 */
const SOURCE_META: Record<
  ClassificationSource,
  { label: string; icon: typeof Scale; className: string; hint: string }
> = {
  regla: {
    label: "Regla",
    icon: Scale,
    className: "border-success/25 bg-success-soft text-success",
    hint: "Regla determinista escrita por una persona. Auditable linea a linea.",
  },
  memoria: {
    label: "Memoria",
    icon: Repeat2,
    className: "border-brand/25 bg-brand-soft text-brand",
    hint: "Se clasifico igual que la ultima correccion humana para este proveedor.",
  },
  llm: {
    label: "Claude",
    icon: Bot,
    className: "border-warning/30 bg-warning-soft text-warning",
    hint: "Inferencia del modelo. Sin regla ni memoria previa: revisar el fundamento.",
  },
  humano: {
    label: "Humano",
    icon: UserRoundCheck,
    className: "border-border-strong/60 bg-transparent text-muted-foreground",
    hint: "Clasificado a mano por un revisor.",
  },
}

const CHANNEL_LABELS: Record<ReviewItem["ingestChannel"], string> = {
  sunat: "SUNAT",
  correo: "Correo",
  manual: "Manual",
  banco: "Banco",
}

/**
 * Semaforo de sustento documental.
 *
 * EL COLOR NO ES DECORACION: es lo que separa «hay que archivar un PDF» de «no
 * existe el comprobante fiscal». Lo primero es orden; lo segundo cuesta el
 * credito fiscal y, muchas veces, la deduccion. Pintarlos igual haria que el
 * segundo se perdiera entre el ruido del primero.
 *
 * Lo COMPLETO no se pinta. Un semaforo donde todo tiene color no señala nada:
 * la ausencia de marca es la informacion, igual que en `BalanceStatus`.
 */
function SustentoTag({ estado }: { estado: EstadoSustento }) {
  if (estado === "completo") return null
  const meta = SUSTENTO_META[estado]
  const Icon = estado === "sin_representacion" ? FileWarning : FileX2
  return (
    <span
      title={meta.ayuda}
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-2xs font-medium",
        meta.tono === "aviso"
          ? "border-warning/30 bg-warning-soft text-warning"
          : "border-destructive/30 bg-destructive-soft text-destructive",
      )}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  )
}

/**
 * De donde sale la propuesta — o que NO hay propuesta.
 *
 * `null` no es un caso raro: son 248 de los 347 comprobantes de la cola. Antes
 * se pintaban como «Claude» porque el mapeo ponia «llm» por defecto, y el
 * contador decia «200 propuestas por el modelo» habiendo cero. Quien miraba la
 * cola creia tener 200 propuestas que aprobar.
 */
function SourceTag({ source }: { source: ClassificationSource | null }) {
  if (source === null) {
    return (
      <span
        title={t("Nadie propuso una cuenta para este comprobante: no hay nada que aprobar. Vuelve a clasificarlo o asigna la cuenta a mano.")}
        className="inline-flex items-center gap-1 rounded-sm border border-dashed px-1.5 py-px text-2xs font-medium text-muted-foreground"
      >
        <CircleDashed className="h-3 w-3" />
        {t("Sin propuesta")}</span>
    )
  }
  const meta = SOURCE_META[source]
  const Icon = meta.icon
  return (
    <span
      title={meta.hint}
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-2xs font-medium",
        meta.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  )
}

/** Cifra de cabecera: dato grande, etiqueta chica. Separadas por filete. */
function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: React.ReactNode
  tone?: "default" | "warning" | "destructive"
  hint?: string
}) {
  return (
    <div className="min-w-[5.5rem] px-3 first:pl-0 last:pr-0" title={hint}>
      <div className="eyebrow">{label}</div>
      <div
        className={cn(
          "num text-left text-lg font-semibold leading-tight",
          tone === "warning" && "text-warning",
          tone === "destructive" && "text-destructive",
        )}
      >
        {value}
      </div>
    </div>
  )
}

export function ReviewQueuePage() {
  const { activeTenantId, activeEntityId } = useAuth()
  /*
    EL RANGO DE LA DESCARGA ES EL AÑO EN CURSO, y hay que elegir uno porque la
    cola NO tiene rango: es «todo lo pendiente», sin fecha. Exportar «todo» de
    una sociedad con cinco años cargados es un archivo que nadie pidió, y el
    endpoint exige `desde` y `hasta` justamente para que la decisión sea
    explícita en vez de silenciosa.
  */
  const anioEnCurso = new Date().getFullYear()
  const desdeExport = `${anioEnCurso}-01-01`
  const hastaExport = `${anioEnCurso}-12-31`
  const { can } = useRbac()
  const { term } = useI18n()
  const { idioma } = useVoz()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [query, setQuery] = React.useState("")
  const [source, setSource] = React.useState("todas")
  const [channel, setChannel] = React.useState("todos")
  const [confidence, setConfidence] = React.useState("todas")
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const [resolved, setResolved] = React.useState<Record<string, "aprobado" | "corregido">>({})
  const [correcting, setCorrecting] = React.useState<ReviewItem | null>(null)
  const [bulkOpen, setBulkOpen] = React.useState(false)

  const maxConfidence = confidence === "todas" ? undefined : confidence === "baja" ? 0.75 : 0.9

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["review-queue", activeTenantId, { query, source, channel, maxConfidence }],
    queryFn: () => fetchReviewQueue({ query, source, channel, maxConfidence }),
  })

  const items = React.useMemo(
    () => (data ?? []).filter((item) => !resolved[item.id]),
    [data, resolved],
  )

  // Los dos catalogos que necesita el dialogo de correccion para escribir algo
  // que el backend acepte: `cuenta_local` es un codigo de `account_local` y
  // `centro_costo` es el UUID de un centro real. Las listas fijas que traia la
  // pantalla no existen en la base y hacian fallar toda correccion con 422.
  const accountsQuery = useQuery({
    queryKey: ["accounts", activeTenantId],
    queryFn: fetchAccounts,
  })
  const costCentersQuery = useQuery({
    queryKey: ["cost-centers", activeTenantId],
    queryFn: fetchCostCenters,
  })
  const costCenterOptions = React.useMemo(
    () => (costCentersQuery.data ?? []).map((cc) => ({ code: cc.code, name: cc.name })),
    [costCentersQuery.data],
  )
  const costCenterLookup = React.useMemo(
    () => (costCentersQuery.data ?? []).map((cc) => ({ id: cc.id, codigo: cc.code })),
    [costCentersQuery.data],
  )

  /**
   * APROBAR EXIGE QUE HAYA ALGO QUE APROBAR.
   *
   * `POST /classify/{id}/approve` responde 404 —«ese documento no tiene una
   * clasificacion vigente»— cuando nadie propuso cuenta. El boton se ofrecia
   * igual en las 200 filas: una puerta que no abre, que es exactamente lo que
   * este producto se prohibe. Se excluyen de la seleccion en vez de dejar que
   * la aprobacion en bloque devuelva 200 fallos.
   */
  const seleccionables = React.useMemo(
    () => items.filter((item) => item.source !== null),
    [items],
  )

  const selectedIds = React.useMemo(
    () => seleccionables.filter((item) => selected[item.id]).map((item) => item.id),
    [seleccionables, selected],
  )

  const canEdit = can("edit")
  const hasFilters =
    query.trim().length > 0 || source !== "todas" || channel !== "todos" || confidence !== "todas"

  const stats = React.useMemo(() => {
    // SIN PROPUESTA es su propia cifra, y es la que manda. No es «confianza
    // baja» —el modelo no dudo, no opino— ni «propuesta por el modelo». Las
    // tres se contaban juntas porque el mapeo inventaba una fuente.
    const sinPropuesta = items.filter((item) => item.source === null).length
    const low = items.filter(
      (item) => item.confidence !== null && confidenceTone(item.confidence) === "baja",
    ).length
    const model = items.filter((item) => item.source === "llm").length
    const flagged = items.filter((item) => item.flags.length > 0).length
    const amount = items.reduce((sum, item) => sum + item.total, 0)
    return { low, model, flagged, amount, sinPropuesta }
  }, [items])

  const toggleExpanded = React.useCallback((id: string, next?: boolean) => {
    setExpanded((prev) => ({ ...prev, [id]: next ?? !prev[id] }))
  }, [])

  const toggleSelected = React.useCallback((id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const nav = useTableKeyboardNav({
    count: items.length,
    onActivate: (index) => {
      const item = items[index]
      if (item) toggleExpanded(item.id)
    },
    onToggle: (index) => {
      const item = items[index]
      if (item) toggleSelected(item.id)
    },
    onExpand: (index, next) => {
      const item = items[index]
      if (item) toggleExpanded(item.id, next)
    },
  })

  /*
    LA COLA LLEGA A CERO, y se dice. Cesar, 2026-10-04: «metiendo algo de
    gamificación». Es lo único que esta pantalla celebra, y lo celebra como
    Marty: una frase en primera persona, sin signos de admiración. Vaciar la
    cola es el trabajo entero de la pantalla; que el último «aprobar» se vea
    igual que el primero es desaprovechar el momento en que alguien terminó.
    Se comprueba contra lo que queda EN PANTALLA (`items`), que es lo que la
    persona está viendo, y no contra el total del servidor con otros filtros.
  */
  const avisarSiVacia = (quedan: number) => {
    if (quedan === 0 && items.length > 0) {
      toast({ title: t("Cola en cero"), description: momento("cola_en_cero", idioma) })
    }
  }

  const approveMutation = useMutation({
    mutationFn: (item: ReviewItem) => approveClassification(item.id),
    onSuccess: (_result, item) => {
      setResolved((prev) => ({ ...prev, [item.id]: "aprobado" }))
      toast({
        variant: "success",
        title: "Clasificacion aprobada",
        description: `${item.serie}-${item.numero} · ${item.accountCode} ${item.accountName}`,
      })
      avisarSiVacia(items.filter((i) => i.id !== item.id).length)
      void queryClient.invalidateQueries({ queryKey: ["review-queue"] })
    },
    onError: onWriteError("la clasificacion", "aprobar"),
  })

  const bulkMutation = useMutation({
    mutationFn: (reason: string) => bulkApprove(selectedIds, reason),
    // OJO: el backend aprueba uno por uno y un fallo NO detiene al resto, asi
    // que un 200 puede significar "3 de 10". Decirlo es obligatorio: dar por
    // aprobados los 10 es exactamente el fallo silencioso que esta pantalla no
    // se puede permitir — lo que no se aprobo no se contabiliza y nadie vuelve
    // a mirarlo.
    onSuccess: (result) => {
      const fallidos = new Set(result.failed.map((f) => f.documentId))
      setResolved((prev) => {
        const next = { ...prev }
        selectedIds.forEach((id) => {
          if (!fallidos.has(id)) next[id] = "aprobado"
        })
        return next
      })

      if (result.failed.length > 0) {
        toast({
          variant: "destructive",
          title: `${result.failed.length} de ${selectedIds.length} no se aprobaron`,
          description: `Se aprobaron ${result.approved}. Los que fallaron siguen en la cola: ${result.failed[0].error}`,
        })
      } else {
        toast({
          variant: "success",
          title: `${result.approved} comprobantes aprobados`,
          description: "El motivo quedo registrado en el audit log.",
        })
      }

      setSelected((prev) => {
        const next: Record<string, boolean> = {}
        for (const id of fallidos) if (prev[id]) next[id] = true
        return next
      })
      setBulkOpen(false)
      void queryClient.invalidateQueries({ queryKey: ["review-queue"] })
    },
    onError: onWriteError("la aprobacion en lote", "aprobar"),
  })

  const correctMutation = useMutation({
    mutationFn: (payload: CorrectionPayload) => {
      if (!correcting) throw new Error("Sin comprobante seleccionado")
      return correctClassification(correcting.id, payload, costCenterLookup)
    },
    onSuccess: (_result, payload) => {
      if (correcting) setResolved((prev) => ({ ...prev, [correcting.id]: "corregido" }))
      toast({
        variant: "success",
        title: "Correccion guardada",
        description: `Cuenta ${payload.accountCode}. Se actualizo la memoria del proveedor.`,
      })
      setCorrecting(null)
      void queryClient.invalidateQueries({ queryKey: ["review-queue"] })
    },
    onError: onWriteError("la correccion"),
  })

  const allSelected = seleccionables.length > 0
    && selectedIds.length === seleccionables.length
  const someSelected = selectedIds.length > 0 && !allSelected

  return (
    <div className="space-y-3">
      <PageHeader
        eyebrow="Clasificacion"
        title={t("Cola de revision")}
        description={
          <>
            {t("Todo lo de baja confianza y todo proveedor nuevo.")}{" "}<strong>{t("Aprobar")}</strong> {" "}{t("confirma la propuesta sin cambiar valores y queda con autor y fecha;")}{" "}<strong>corregir</strong>{" "}
            {t("cambia un valor y exige motivo. Cada correccion escribe la memoria de proveedor.")}</>
        }
        actions={
          <span
            className="hidden items-center gap-1.5 rounded-sm border bg-surface-sunken px-2 py-1 text-2xs text-muted-foreground lg:inline-flex"
            title={t("Navegacion por teclado en la tabla")}
          >
            <Keyboard className="h-3 w-3" />
            <Key>↑</Key>
            <Key>↓</Key> {" "}{t("mover ·")}{" "}<Key>→</Key> {" "}{t("fundamento ·")}{" "}<Key>espacio</Key> {" "}{t("marcar ·")}{" "}
            <Key>enter</Key> abrir
          </span>
        }
      />

      {/* Resumen de la cola: cuatro cifras, no cuatro tarjetas. */}
      <div className="flex flex-wrap items-center divide-x divide-border rounded-md border bg-surface px-3 py-2">
        <Stat label={t("Pendientes")} value={items.length} />
        <Stat
          label={t("Sin propuesta")}
          value={stats.sinPropuesta}
          tone={stats.sinPropuesta > 0 ? "destructive" : "default"}
          hint="Nadie clasifico estos comprobantes: no hay nada que aprobar. Hay que volver a clasificarlos o asignarles la cuenta a mano."
        />
        <Stat
          label={t("Confianza < 75%")}
          value={stats.low}
          tone={stats.low > 0 ? "destructive" : "default"}
          hint="Revisar contra el comprobante fisico antes de aprobar."
        />
        <Stat
          label={t("Propuestas por el modelo")}
          value={stats.model}
          tone={stats.model > 0 ? "warning" : "default"}
          hint="Sin regla ni memoria previa: leer el fundamento."
        />
        <Stat label={t("Con marcas")} value={stats.flagged} hint="Detraccion, duplicado, monto atipico." />
        <Stat label={t("Importe en cola")} value={formatAmount(stats.amount)} />
      </div>

      <Toolbar>
        <ToolbarField label={t("Buscar")} htmlFor="q-review" className="min-w-[18rem] flex-1">
          <SearchInput
            id="q-review"
            value={query}
            onValueChange={setQuery}
            placeholder={`Proveedor, ${term.identificador_fiscal}, serie-numero o cuenta`}
            className="w-full"
          />
        </ToolbarField>

        <ToolbarField label={t("Origen")} htmlFor="f-origen">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger id="f-origen" className="w-44">
              <SelectValue placeholder={t("Origen")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">{t("Todos los origenes")}</SelectItem>
              <SelectItem value="regla">{t("Regla")}</SelectItem>
              <SelectItem value="memoria">{t("Memoria de proveedor")}</SelectItem>
              <SelectItem value="llm">{t("Claude")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Canal")} htmlFor="f-canal">
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger id="f-canal" className="w-40">
              <SelectValue placeholder={t("Canal")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t("Todos los canales")}</SelectItem>
              <SelectItem value="sunat">{term.autoridad}</SelectItem>
              <SelectItem value="correo">{t("Correo")}</SelectItem>
              <SelectItem value="manual">{t("Carga manual")}</SelectItem>
              <SelectItem value="banco">{t("Banco")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Confianza")} htmlFor="f-conf">
          <Select value={confidence} onValueChange={setConfidence}>
            <SelectTrigger id="f-conf" className="w-44">
              <SelectValue placeholder={t("Confianza")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">{t("Cualquier confianza")}</SelectItem>
              <SelectItem value="baja">{t("Menor a 75%")}</SelectItem>
              <SelectItem value="media">{t("Menor a 90%")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>

        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQuery("")
              setSource("todas")
              setChannel("todos")
              setConfidence("todas")
            }}
          >
            <X />
            {t("Limpiar")}</Button>
        ) : null}
      </Toolbar>

      {/* Barra de accion en bloque: aparece solo cuando hay algo seleccionado. */}
      {selectedIds.length > 0 ? (
        <div className="animate-alert-in flex flex-wrap items-center gap-3 rounded-md border border-brand/30 bg-brand-soft px-3 py-2">
          <span className="text-sm font-medium text-foreground">
            <span className="num font-semibold">{selectedIds.length}</span> {" "}{t("comprobante(s) seleccionado(s)")}</span>
          <span className="text-2xs text-muted-foreground">
            {t("La aprobacion en bloque exige motivo: son asientos que van al libro.")}</span>
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => setSelected({})}>
              {t("Deseleccionar")}</Button>
            <Button size="sm" disabled={!canEdit} onClick={() => setBulkOpen(true)}>
              <Check />
              {t("Aprobar seleccionados")}</Button>
          </div>
        </div>
      ) : null}

      <WorkPanel
        actions={
          /*
            Se descargan TODOS los comprobantes del año, no sólo los pendientes
            que se ven en la cola. La cola es un filtro de trabajo —«lo que me
            falta despachar»— y un archivo con ese filtro dentro se lee, dos
            semanas después, como «éstos son los comprobantes del año».
          */
          <BotonDescargar
            recurso="comprobantes"
            etiqueta={`Descargar ${anioEnCurso}`}
            filtros={{ entity_id: activeEntityId, desde: desdeExport, hasta: hastaExport }}
          />
        }
        title={t("Comprobantes pendientes")}
        meta={
          <Badge variant="outline" mono>
            {items.length}
          </Badge>
        }
        footnote={
          <>
            {t("Los filtros viajan al backend como query params. Aprobar no modifica ningun valor: solo confirma la propuesta del motor y la deja firmada con tu nombre y la fecha.")}</>
        }
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={items.length === 0}
          emptyMessage={
            hasFilters
              ? "Ningun comprobante coincide con estos filtros."
              : "No queda nada por revisar."
          }
          emptyHint={
            hasFilters
              ? "Prueba a ampliar el rango de confianza o a quitar el filtro de origen."
              : "El motor clasifico todo con confianza suficiente y sin proveedores nuevos. Los comprobantes ya estan asentados en los tres libros."
          }
          emptyAction={
            hasFilters ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setQuery("")
                  setSource("todas")
                  setChannel("todos")
                  setConfidence("todas")
                }}
              >
                {t("Quitar filtros")}</Button>
            ) : null
          }
          columns={[4, 10, 8, 20, 10, 10, 10, 12, 8]}
        >
          <Table
            className="table-fixed min-w-[89rem]"
            containerClassName="max-h-[calc(100vh-22rem)]"
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(checked) => {
                      const next: Record<string, boolean> = {}
                      // Solo lo APROBABLE: marcar los 200 sin propuesta daria
                      // 200 fallos al aprobar en bloque, y el contador de
                      // seleccionados mentiria sobre cuantos van a entrar.
                      if (checked) seleccionables.forEach((item) => (next[item.id] = true))
                      setSelected(next)
                    }}
                    aria-label={t("Seleccionar todos los comprobantes")}
                  />
                </TableHead>
                <TableHead className="w-6" />
                {/*
                  ANCHOS MEDIDOS, no estimados. Con las cifras tabulares de
                  Onest a 12px y el padding de 8px por lado:
                    «17/07/2026»        92 px  ->  w-24        (96)
                    «TIPO» (cabecera)   48 px  ->  w-14        (56)
                    «FN01-48698988»    118 px  ->  w-[7.5rem] (120)
                    «CNC0703273B3»     114 px  ->  w-[7.5rem] (120)
                  Los cuatro estaban por debajo de lo que su propio contenido
                  necesita, y por eso se invadian. El RUC peruano son 11 digitos
                  pero el RFC mexicano y el CNPJ brasileno son mas largos: la
                  columna se dimensiona por el mas ancho de los cuatro paises,
                  no por el del pais de quien la escribio.
                */}
                <TableHead className="w-24">{t("Fecha")}</TableHead>
                <TableHead className="w-14">{t("Tipo")}</TableHead>
                <TableHead className="w-[7.5rem]">{t("Documento")}</TableHead>
                <TableHead className="w-[7.5rem]">{term.identificador_fiscal}</TableHead>
                <TableHead className="w-[9.5rem]">{t("Proveedor")}</TableHead>
                <TableHead className="w-[4.5rem] text-right">{t("Neto")}</TableHead>
                <TableHead className="w-[4.5rem] text-right">{term.impuesto_ventas}</TableHead>
                <TableHead className="w-[6.25rem] text-right">{t("Total")}</TableHead>
                <TableHead>{t("Cuenta propuesta")}</TableHead>
                <TableHead className="w-[11rem]">{t("Qué es · en Finanzas")}</TableHead>
                <TableHead className="w-[4.25rem]">{t("CC")}</TableHead>
                <TableHead className="w-[5.25rem]">{t("Origen")}</TableHead>
                <TableHead className="w-[5.5rem] text-right">{t("Confianza")}</TableHead>
                <TableHead className={cn("w-[10.75rem] text-right", stickyActionHead)}>{t("Acciones")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody {...nav.bodyProps}>
              {items.map((item, index) => {
                const isExpanded = Boolean(expanded[item.id])
                const isSelected = Boolean(selected[item.id])
                const tone = item.confidence === null
                  ? "baja"
                  : confidenceTone(item.confidence)
                return (
                  <React.Fragment key={item.id}>
                    <TableRow
                      {...nav.rowProps(index)}
                      data-state={isSelected ? "selected" : undefined}
                      aria-expanded={isExpanded}
                      className={cn(
                        "outline-none",
                        nav.cursor === index && "row-cursor",
                        tone === "baja" && "shadow-[inset_-2px_0_0_0_hsl(var(--destructive)/0.55)]",
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          disabled={item.source === null}
                          onCheckedChange={() => toggleSelected(item.id)}
                          aria-label={`Seleccionar ${item.serie}-${item.numero}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(item.id)}
                          className="grid h-4 w-4 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          aria-label={isExpanded ? "Ocultar fundamento" : "Ver fundamento"}
                          aria-expanded={isExpanded}
                        >
                          <ChevronRight
                            className={cn(
                              "h-3.5 w-3.5 transition-transform duration-150",
                              isExpanded && "rotate-90",
                            )}
                          />
                        </button>
                      </TableCell>
                      <TableCell className="code whitespace-nowrap text-muted-foreground">
                        {formatDate(item.issueDate)}
                      </TableCell>
                      <TableCell className="code text-muted-foreground">{item.docType}</TableCell>
                      {/*
                        COMENTAR UN COMPROBANTE. Va junto a su serie-número, que
                        es como lo nombra todo el mundo. Es distinto de corregir
                        la clasificación: eso se hace en la fila; esto es
                        preguntarle algo a quien la va a aprobar.
                      */}
                      <TableCell
                        className="code truncate font-medium"
                        title={`${item.serie}-${item.numero}`}
                      >
                        <span className="flex items-center gap-1">
                          <span className="truncate">{item.serie}-{item.numero}</span>
                          <BotonComentario
                            ancla={{
                              ambito: "documento",
                              referencia: refDocumento(item.id),
                              rotulo: `${item.serie}-${item.numero}`,
                            }}
                          />
                        </span>
                      </TableCell>
                      <TableCell className="code truncate text-muted-foreground" title={item.supplierRuc}>
                        {item.supplierRuc}
                      </TableCell>
                      <TableCell className="truncate font-medium" title={item.supplierName}>
                        {item.supplierName}
                      </TableCell>
                      <TableCell className="num text-muted-foreground">
                        {formatAmount(item.net)}
                      </TableCell>
                      <TableCell className="num text-muted-foreground">
                        {formatAmount(item.igv)}
                      </TableCell>
                      <TableCell className="num-strong whitespace-nowrap">
                        {formatAmount(item.total)}
                        {item.currency !== "PEN" ? (
                          <span className="ml-1 text-2xs font-normal text-muted-foreground">
                            {item.currency}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="truncate whitespace-nowrap">
                        <span className="code font-medium">{item.accountCode}</span>{" "}
                        <span className="text-muted-foreground">{item.accountName}</span>
                      </TableCell>
                      {/*
                        QUÉ ES Y A DÓNDE VA. Cesar: «debería de salir si es ventas
                        o costos o gastos, aparte de precalificarlo a nivel
                        contable como a nivel financiero». La cuenta local es la
                        calificación CONTABLE; esto es la FINANCIERA: la
                        naturaleza y la línea del P&L de gestión a la que va.
                        «Sin mapear» va en ámbar: un comprobante aprobado así se
                        asienta y no aparece en ningún estado.
                      */}
                      <TableCell className="whitespace-nowrap text-2xs">
                        {item.accountCode ? (
                          item.naturaleza ? (
                            <span className="flex flex-col leading-4">
                              <Badge
                                variant={item.naturaleza === "ingreso" ? "success-soft"
                                  : item.naturaleza === "costo" || item.naturaleza === "gasto" ? "outline" : "secondary"}
                                className="w-fit"
                              >
                                {t(NATURALEZA_LABELS[item.naturaleza])}
                              </Badge>
                              <span className="truncate text-muted-foreground" title={item.grupoNombre}>
                                {item.lineaFinanzas || item.grupoNombre}
                              </span>
                            </span>
                          ) : (
                            <span className="text-warning" title={t("La cuenta propuesta no mapea a ninguna cuenta de grupo: aprobada así, no entra en ningún estado.")}>
                              {t("sin mapear")}
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="code whitespace-nowrap text-2xs text-muted-foreground">
                        {item.costCenter ?? "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <SourceTag source={item.source} />
                          <SustentoTag estado={item.sustento} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.confidence === null ? (
                          <span className="text-2xs text-muted-foreground">—</span>
                        ) : (
                          <ConfidenceMeter value={item.confidence} />
                        )}
                      </TableCell>
                      <TableCell className={stickyActionCell}>
                        <div className="flex justify-end gap-1 opacity-80 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100">
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={!canEdit || approveMutation.isPending
                                      || item.source === null}
                            title={item.source === null
                              ? "No hay ninguna propuesta que confirmar. Usa «Corregir» para asignarle la cuenta."
                              : undefined}
                            onClick={() => approveMutation.mutate(item)}
                          >
                            <Check />
                            {t("Aprobar")}</Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            disabled={!canEdit}
                            onClick={() => setCorrecting(item)}
                          >
                            <Pencil />
                            {t("Corregir")}</Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {isExpanded ? (
                      <TableRow className="bg-surface-sunken/70 hover:bg-surface-sunken/70">
                        <TableCell colSpan={15} className="h-auto bg-inherit p-0">
                          <Rationale item={item} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {/*
        LO QUE LLEGÓ SIN SU XML va justo debajo de la cola: es lo otro que hay
        que despachar después de un envío por correo, y antes no salía en
        ninguna pantalla.

        Aquí debajo había un panel de «salud del motor» —qué capa resuelve cada
        comprobante, cuánto se corrige a mano—. Es una métrica del PRODUCTO, no
        de la contabilidad del cliente: a quien despacha la cola no le cambia
        ninguna decisión, y el Dashboard ya la enseñaba con los mismos datos
        (`/classify/metrics`). Dos sitios para la misma tabla, y éste no era el
        suyo.
      */}
      <SinComprobante />

      <CorrectionDialog
        item={correcting}
        open={Boolean(correcting)}
        onOpenChange={(open) => {
          if (!open) setCorrecting(null)
        }}
        accounts={accountsQuery.data}
        costCenters={costCenterOptions}
        pending={correctMutation.isPending}
        onSubmit={(payload) => correctMutation.mutateAsync(payload).then(() => undefined)}
      />

      <ReasonDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        title={`Aprobar ${selectedIds.length} clasificaciones`}
        description={t("La aprobacion masiva se registra con motivo: es una accion en bloque sobre asientos que iran al libro.")}
        summary={
          <span>
            {t("Se aprobaran")}{" "}<strong>{selectedIds.length}</strong> {" "}{t("comprobantes con la cuenta y el tratamiento propuestos por el motor.")}</span>
        }
        confirmLabel={t("Aprobar con motivo")}
        suggestions={[
          "Revisadas una por una contra el comprobante fisico.",
          "Proveedores recurrentes ya validados en periodos anteriores.",
        ]}
        pending={bulkMutation.isPending}
        onConfirm={(reason) => bulkMutation.mutateAsync(reason).then(() => undefined)}
      />
    </div>
  )
}

/**
 * Fundamento expandible.
 *
 * No es un parrafo suelto: es la cadena de evidencia que sostiene la
 * propuesta. Quien aprueba tiene que poder responder "por que esta cuenta y no
 * otra" sin abrir otra pantalla, porque su nombre queda en el asiento.
 */
function Rationale({ item }: { item: ReviewItem }) {
  // Sin fuente no hay cadena de evidencia que mostrar, y fingir una —la del
  // modelo, que es lo que hacia el default— es lo contrario de lo que este
  // panel existe para dar.
  const meta = item.source === null ? null : SOURCE_META[item.source]
  const Icon = meta?.icon ?? CircleDashed
  return (
    <div className="animate-row-in border-l-2 border-brand/50 px-3 py-3">
      <div className="grid gap-x-6 gap-y-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h4 className="eyebrow mb-1 flex items-center gap-1.5">
            <Icon className="h-3 w-3" />
            {meta ? `Fundamento · ${meta.label}` : "Sin propuesta"}
          </h4>
          {meta ? (
            <>
              <p className="max-w-[85ch] text-xs leading-[1.25rem] text-foreground">
                {item.rationale}
              </p>
              <p className="mt-1.5 text-2xs text-muted-foreground">{meta.hint}</p>
            </>
          ) : (
            /*
              Ni regla, ni memoria, ni modelo: nadie clasifico este comprobante.
              Decirlo es lo unico honesto — el panel existe para que quien
              aprueba pueda responder «por que esta cuenta y no otra», y aqui no
              hay cuenta ni hay porque.
            */
            <p className="max-w-[85ch] text-xs leading-[1.25rem] text-muted-foreground">
              {t("Nadie clasifico este comprobante: no hay regla que lo case, no hay memoria de este proveedor y el modelo no dejo propuesta. No hay nada que aprobar — vuelve a clasificarlo o asignale la cuenta a mano con")}{" "}<strong>{t("Corregir")}</strong>.
            </p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-sm border bg-surface px-1.5 py-0.5 text-2xs">
              <FileText className="h-3 w-3 text-muted-foreground" />
              <span className="code font-medium">{item.accountCode}</span>
              <span className="text-muted-foreground">{item.accountName}</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-sm border bg-surface px-1.5 py-0.5 text-2xs">
              <Landmark className="h-3 w-3 text-muted-foreground" />
              {item.taxTreatment}
            </span>
            {item.detractionCode ? (
              <span className="inline-flex items-center gap-1 rounded-sm border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-2xs text-warning">
                <Sparkles className="h-3 w-3" />
                {t("Detraccion")}{" "}{item.detractionCode} ·{" "}
                {Math.round((item.detractionRate ?? 0) * 100)}%
              </span>
            ) : null}
          </div>
        </div>

        <dl className="space-y-1.5 text-2xs lg:border-l lg:pl-6">
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 uppercase tracking-[0.06em] text-muted-foreground">
              {t("Pipeline run")}</dt>
            <dd className="code min-w-0 truncate">{item.pipelineRunId}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 uppercase tracking-[0.06em] text-muted-foreground">
              {t("Canal")}</dt>
            <dd>{CHANNEL_LABELS[item.ingestChannel]}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-24 shrink-0 uppercase tracking-[0.06em] text-muted-foreground">
              {t("Confianza")}</dt>
            <dd>
              {item.confidence === null ? (
                <span className="text-xs text-muted-foreground">
                  {t("No hay clasificacion, asi que no hay confianza que medir.")}</span>
              ) : (
                <ConfidenceMeter value={item.confidence} className="justify-start" />
              )}
            </dd>
          </div>
          {item.flags.length > 0 ? (
            <div className="flex gap-2 pt-0.5">
              <dt className="w-24 shrink-0 uppercase tracking-[0.06em] text-muted-foreground">
                {t("Marcas")}</dt>
              <dd className="flex min-w-0 flex-wrap gap-1">
                {item.flags.map((flag) => (
                  <span
                    key={flag}
                    className="inline-flex items-center gap-1 rounded-sm border border-warning/30 bg-warning-soft px-1 py-px text-warning"
                  >
                    <Flag className="h-2.5 w-2.5" />
                    {flag.replace(/_/g, " ")}
                  </span>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </div>
  )
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-[3px] border border-border-strong/70 bg-surface px-1 font-sans text-2xs font-medium text-foreground shadow-xs">
      {children}
    </kbd>
  )
}
